import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { activateTrial } from '../lib/api';
import { Loader2, Check, Crown, AlertCircle } from 'lucide-react';

export default function ActivateTrial() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();
  const token = params.get('t') || params.get('token') || '';
  const [state, setState] = useState('loading'); // loading | activated | already | error
  const [message, setMessage] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against double-invoke in StrictMode
    ran.current = true;
    if (!token) {
      setState('error');
      setMessage('This activation link is missing its code. Check the link in your email.');
      return;
    }
    (async () => {
      try {
        const res = await activateTrial(token);
        if (res.data?.status === 'already_subscribed') {
          setState('already');
          setMessage(res.data.message || "You're already on a paid plan.");
        } else {
          setState('activated');
        }
      } catch (err) {
        setState('error');
        setMessage(err.response?.data?.detail || 'We could not activate this link. It may have already been used.');
      }
    })();
  }, [token]);

  const goUse = () => {
    if (isAuthenticated) navigate('/measure');
    else openAuthModal('login');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <Helmet>
        <title>Activate Your 7-Day Contractor Trial | SquareCount</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Navbar />

      <section className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="max-w-md w-full text-center">
          {state === 'loading' && (
            <div data-testid="activate-loading">
              <Loader2 className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-4" />
              <p className="text-slate-300">Activating your trial…</p>
            </div>
          )}

          {state === 'activated' && (
            <div data-testid="activate-success">
              <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading font-black text-3xl text-white mb-3">You're in. 7 days of Contractor, on us.</h1>
              <p className="text-slate-300 mb-2">
                Your full Contractor toolkit is unlocked for 7 days — shingle, tile &amp; metal lists,
                valley &amp; ridge takeoffs, editable reports, custom materials, the job cost estimator,
                and shareable client reports.
              </p>
              <p className="text-slate-500 text-sm mb-8">No card required. It reverts to Free automatically when the trial ends.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={goUse} className="bg-orange-500 hover:bg-orange-400 text-white h-12 px-8 font-heading font-bold tracking-wide">
                  {isAuthenticated ? 'Start Measuring' : 'Log In to Start'}
                </Button>
                <Button onClick={() => navigate('/features')} variant="outline" className="h-12 px-8 font-heading font-bold border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
                  See What's New
                </Button>
              </div>
            </div>
          )}

          {state === 'already' && (
            <div data-testid="activate-already">
              <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="font-heading font-bold text-2xl text-white mb-3">You're already covered</h1>
              <p className="text-slate-300 mb-8">{message}</p>
              <Button onClick={goUse} className="bg-orange-500 hover:bg-orange-400 text-white h-12 px-8 font-heading font-bold tracking-wide">
                {isAuthenticated ? 'Go to Dashboard' : 'Log In'}
              </Button>
            </div>
          )}

          {state === 'error' && (
            <div data-testid="activate-error">
              <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <AlertCircle className="w-8 h-8 text-orange-400" />
              </div>
              <h1 className="font-heading font-bold text-2xl text-white mb-3">Link couldn't be activated</h1>
              <p className="text-slate-300 mb-8">{message}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate('/pricing')} className="bg-orange-500 hover:bg-orange-400 text-white h-12 px-8 font-heading font-bold tracking-wide">
                  Start a Free Trial
                </Button>
                <Button onClick={() => navigate('/contact')} variant="outline" className="h-12 px-8 font-heading font-bold border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
                  Contact Support
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
